/**
 * HTML marker utilities for the JSON leaf review modal: HTML tags inside string
 * values are shown as numbered chips (tooltip = original tag) and converted back
 * to real HTML with toStorageHtml() on save. Placeholders like {{name}}, {0} and
 * ${name} are never treated as tags. HTML entities in text nodes are preserved
 * via atomic entity markers (display = decoded character, storage = original spelling).
 */

const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta',
  'param', 'source', 'track', 'wbr',
]);

const PLACEHOLDER_PATTERNS = [
  { regex: /\{\{[^}]+\}\}/g },
  { regex: /\$\{[^}]+\}/g },
  { regex: /\{[0-9]+\}/g },
];

const ENTITY_REGEX = /&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g;

const MARKER_OPEN = (id) => `\uE000M${id}O\uE001`;
const MARKER_CLOSE = (id) => `\uE000M${id}C\uE001`;
const MARKER_REGEX = /\uE000M(\d+)([OC])\uE001/g;

const ENTITY_MARKER = (id) => `\uE004EN${id}\uE005`;
const ENTITY_MARKER_REGEX = /\uE004EN(\d+)\uE005/g;

const COMBINED_MARKER_REGEX = /\uE000M(\d+)([OC])\uE001|\uE004EN(\d+)\uE005/g;

function getParser() {
  return new DOMParser();
}

function escapeAttrValue(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

function buildOpenTagString(tagName, attributes) {
  const attrs = attributes || {};
  const parts = Object.keys(attrs).map((key) => {
    const val = attrs[key];
    if (val === '' || val === true) return key;
    return `${key}="${escapeAttrValue(val)}"`;
  });
  const attrStr = parts.length ? ` ${parts.join(' ')}` : '';
  return `<${tagName}${attrStr}>`;
}

function buildCloseTagString(tagName) {
  return `</${tagName}>`;
}

function readElementAttributes(element) {
  const attrs = {};
  Array.from(element.attributes || []).forEach((attr) => {
    attrs[attr.name] = attr.value;
  });
  return attrs;
}

function decodeAngleBracketEntities(text) {
  return String(text || '').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

function decodeEntityChar(entity) {
  const el = document.createElement('textarea');
  el.innerHTML = entity;
  return el.value;
}

function maskPlaceholders(text) {
  let masked = text;
  const tokens = [];
  PLACEHOLDER_PATTERNS.forEach(({ regex }) => {
    masked = masked.replace(regex, (match) => {
      const token = `\uE002PH_${tokens.length}\uE003`;
      tokens.push({ token, original: match });
      return token;
    });
  });
  return { masked, tokens };
}

function unmaskPlaceholders(text, tokens) {
  let out = text;
  tokens.forEach(({ token, original }) => {
    out = out.split(token).join(original);
  });
  return out;
}

function maskEntities(text) {
  const entityMap = {};
  let nextId = 1;
  const masked = String(text || '').replace(ENTITY_REGEX, (match) => {
    const id = nextId;
    nextId += 1;
    entityMap[id] = { original: match, decoded: decodeEntityChar(match) };
    return ENTITY_MARKER(id);
  });
  return { masked, entityMap };
}

function hasElementNodes(doc) {
  const body = doc.body;
  if (!body) return false;
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_ELEMENT);
  return walker.nextNode() !== null;
}

export function containsHtml(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = decodeAngleBracketEntities(text).trim();
  if (!trimmed.includes('<')) return false;
  const { masked } = maskPlaceholders(trimmed);
  const doc = getParser().parseFromString(`<div>${masked}</div>`, 'text/html');
  return hasElementNodes(doc);
}

function createTagMapEntry(element) {
  const tagName = element.tagName.toLowerCase();
  const attributes = readElementAttributes(element);
  const isVoid = VOID_TAGS.has(tagName);
  return {
    tagName,
    attributes,
    openTag: buildOpenTagString(tagName, attributes),
    closeTag: isVoid ? '' : buildCloseTagString(tagName),
    isVoid,
  };
}

export function getChipTooltip(tagMap, markerId, side) {
  const entry = tagMap[markerId];
  if (!entry) return `[${markerId}]`;
  if (side === 'close') {
    return entry.closeTag || entry.openTag;
  }
  return entry.openTag;
}

function parseHtmlFragment(html) {
  const doc = getParser().parseFromString(html, 'text/html');
  return doc.body;
}

function collectElementsInOrder(root) {
  const list = [];
  const walk = (element) => {
    list.push(element);
    Array.from(element.children).forEach(walk);
  };
  Array.from(root.children).forEach(walk);
  return list;
}

function serializeNodeWithIds(node, elementIdMap) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const element = node;
  const id = elementIdMap.get(element);
  const tagName = element.tagName.toLowerCase();
  if (id === undefined) {
    return Array.from(element.childNodes).map((c) => serializeNodeWithIds(c, elementIdMap)).join('');
  }
  if (VOID_TAGS.has(tagName)) {
    return MARKER_OPEN(id) + MARKER_CLOSE(id);
  }
  const inner = Array.from(element.childNodes)
    .map((c) => serializeNodeWithIds(c, elementIdMap))
    .join('');
  return MARKER_OPEN(id) + inner + MARKER_CLOSE(id);
}

function buildMarkedModelFromHtml(html) {
  const decoded = decodeAngleBracketEntities(html || '');
  const { masked: placeholderMasked, tokens } = maskPlaceholders(decoded);
  const { masked, entityMap } = maskEntities(placeholderMasked);

  if (!masked.trim()) {
    return { displayWithMarkers: '', tagMap: {}, entityMap: {}, sourceElementIds: [] };
  }

  if (!containsHtml(masked)) {
    return {
      displayWithMarkers: unmaskPlaceholders(masked, tokens),
      tagMap: {},
      entityMap,
      sourceElementIds: [],
    };
  }

  const root = parseHtmlFragment(masked);
  const elements = collectElementsInOrder(root);
  const tagMap = {};
  const elementIdMap = new Map();
  const sourceElementIds = [];

  elements.forEach((el, index) => {
    const id = index + 1;
    elementIdMap.set(el, id);
    tagMap[id] = createTagMapEntry(el);
    sourceElementIds.push({ id, tagName: el.tagName.toLowerCase() });
  });

  let display = '';
  Array.from(root.childNodes).forEach((child) => {
    display += serializeNodeWithIds(child, elementIdMap);
  });
  display = unmaskPlaceholders(display, tokens);

  return { displayWithMarkers: display, tagMap, entityMap, sourceElementIds };
}

function buildTranslationModel(sourceHtml, translationHtml, sourceTagMap, sourceElementIds) {
  const decoded = decodeAngleBracketEntities(translationHtml || '');
  const { masked: placeholderMasked, tokens } = maskPlaceholders(decoded);
  const { masked, entityMap } = maskEntities(placeholderMasked);
  const tagMap = { ...sourceTagMap };
  let nextId = Math.max(0, ...Object.keys(tagMap).map(Number)) + 1;

  if (!masked.trim()) {
    return { displayWithMarkers: '', tagMap, entityMap };
  }

  if (!containsHtml(masked)) {
    return {
      displayWithMarkers: unmaskPlaceholders(masked, tokens),
      tagMap,
      entityMap,
    };
  }

  const root = parseHtmlFragment(masked);
  const transElements = collectElementsInOrder(root);
  const elementIdMap = new Map();
  const tagNameUsage = {};

  transElements.forEach((el) => {
    const tagName = el.tagName.toLowerCase();
    const usage = tagNameUsage[tagName] || 0;
    tagNameUsage[tagName] = usage + 1;

    const sourceMatches = sourceElementIds.filter((s) => s.tagName === tagName);
    const sourceMatch = sourceMatches[usage];

    let id;
    if (sourceMatch) {
      id = sourceMatch.id;
      if (!tagMap[id]) {
        tagMap[id] = createTagMapEntry(el);
      }
    } else {
      id = nextId;
      nextId += 1;
      tagMap[id] = createTagMapEntry(el);
    }
    elementIdMap.set(el, id);
  });

  let display = '';
  Array.from(root.childNodes).forEach((child) => {
    display += serializeNodeWithIds(child, elementIdMap);
  });
  display = unmaskPlaceholders(display, tokens);

  return { displayWithMarkers: display, tagMap, entityMap };
}

export function buildDisplayModels(sourceText, translationText) {
  const source = sourceText || '';
  const translation = translationText || '';
  const sourceModel = buildMarkedModelFromHtml(source);

  if (!translation.trim()) {
    return {
      source: {
        displayWithMarkers: sourceModel.displayWithMarkers,
        tagMap: sourceModel.tagMap,
        entityMap: sourceModel.entityMap,
      },
      translation: {
        displayWithMarkers: '',
        tagMap: { ...sourceModel.tagMap },
        entityMap: {},
      },
    };
  }

  const translationModel = buildTranslationModel(
    source,
    translation,
    sourceModel.tagMap,
    sourceModel.sourceElementIds,
  );

  return {
    source: {
      displayWithMarkers: sourceModel.displayWithMarkers,
      tagMap: sourceModel.tagMap,
      entityMap: sourceModel.entityMap,
    },
    translation: {
      displayWithMarkers: translationModel.displayWithMarkers,
      tagMap: translationModel.tagMap,
      entityMap: translationModel.entityMap,
    },
  };
}

function replaceEntityMarkers(text, entityMap) {
  if (!text) return '';
  return text.replace(ENTITY_MARKER_REGEX, (match, idStr) => {
    const id = Number(idStr);
    const entry = entityMap[id];
    return entry ? entry.original : match;
  });
}

export function toStorageHtml(displayWithMarkers, tagMap, entityMap = {}) {
  if (!displayWithMarkers) return '';
  const withTags = displayWithMarkers.replace(MARKER_REGEX, (match, idStr, side) => {
    const id = Number(idStr);
    const entry = tagMap[id];
    if (!entry) return match;
    if (side === 'O') {
      return entry.openTag;
    }
    if (entry.isVoid) return '';
    return entry.closeTag || '';
  });
  return replaceEntityMarkers(withTags, entityMap || {});
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

export function tokenizeDisplayUnits(displayWithMarkers) {
  const units = [];
  const text = displayWithMarkers || '';
  if (!text) return units;
  const re = new RegExp(COMBINED_MARKER_REGEX.source, 'g');
  let lastIndex = 0;
  let match = re.exec(text);
  while (match) {
    for (let i = lastIndex; i < match.index; i += 1) {
      units.push(text[i]);
    }
    units.push(match[0]);
    lastIndex = match.index + match[0].length;
    match = re.exec(text);
  }
  for (let i = lastIndex; i < text.length; i += 1) {
    units.push(text[i]);
  }
  return units;
}

const SINGLE_MARKER_REGEX = /\uE000M(\d+)([OC])\uE001/;
const SINGLE_ENTITY_MARKER_REGEX = /\uE004EN(\d+)\uE005/;

function markerTokenToEditorHtml(token, tagMap, entityMap) {
  const markerMatch = SINGLE_MARKER_REGEX.exec(token);
  if (markerMatch) {
    const id = Number(markerMatch[1]);
    const side = markerMatch[2];
    const tooltip = getChipTooltip(tagMap, id, side === 'O' ? 'open' : 'close');
    return `<span class="html-marker-chip" contenteditable="false" data-marker-id="${id}" data-marker-side="${side}" title="${escapeAttr(tooltip)}" aria-label="${escapeAttr(tooltip)}">${id}</span>`;
  }
  const entityMatch = SINGLE_ENTITY_MARKER_REGEX.exec(token);
  if (entityMatch) {
    const id = Number(entityMatch[1]);
    const entry = (entityMap || {})[id];
    const decoded = entry ? entry.decoded : '';
    return `<span class="html-entity-marker" contenteditable="false" data-entity-id="${id}">${escapeHtml(decoded)}</span>`;
  }
  return escapeHtml(token);
}

function isMarkerUnit(unit) {
  return SINGLE_MARKER_REGEX.test(unit) || SINGLE_ENTITY_MARKER_REGEX.test(unit);
}

function unitsToEditorHtml(units, tagMap, entityMap) {
  return units.map((unit) => (
    isMarkerUnit(unit)
      ? markerTokenToEditorHtml(unit, tagMap, entityMap)
      : escapeHtml(unit)
  )).join('');
}

export function displayToEditorHtml(displayWithMarkers, tagMap, entityMap = {}, highlightOptions = null) {
  if (!displayWithMarkers) return '';
  const units = tokenizeDisplayUnits(displayWithMarkers);
  const highlightStart = highlightOptions?.start;
  const highlightEnd = highlightOptions?.end;
  const tooltipLabel = highlightOptions?.tooltipLabel || '';
  const hasHighlight = typeof highlightStart === 'number'
    && typeof highlightEnd === 'number'
    && highlightEnd > highlightStart;

  let html = '';
  let index = 0;
  while (index < units.length) {
    const inRange = hasHighlight && index >= highlightStart && index < highlightEnd;
    if (!inRange) {
      html += unitsToEditorHtml([units[index]], tagMap, entityMap);
      index += 1;
      continue;
    }
    const slice = units.slice(index, Math.min(highlightEnd, units.length));
    const inner = unitsToEditorHtml(slice, tagMap, entityMap);
    const aria = escapeAttr(tooltipLabel);
    html += (
      `<span class="last-edit-highlight" tabindex="0" aria-label="${aria}">`
      + `<span class="last-edit-tooltip" role="tooltip">${escapeHtml(tooltipLabel)}</span>`
      + inner
      + '</span>'
    );
    index += slice.length;
  }
  return html;
}

function serializeEditorNode(node, tagMap, idCounter) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const el = node;
  if (el.classList && el.classList.contains('html-marker-chip')) {
    const id = Number(el.getAttribute('data-marker-id'));
    const side = el.getAttribute('data-marker-side');
    if (side === 'O') return MARKER_OPEN(id);
    return MARKER_CLOSE(id);
  }
  if (el.classList && el.classList.contains('html-entity-marker')) {
    const id = Number(el.getAttribute('data-entity-id'));
    return ENTITY_MARKER(id);
  }

  const tagName = el.tagName.toLowerCase();
  if (tagName === 'br') return '<br>';
  if (el.classList && el.classList.contains('last-edit-tooltip')) {
    return '';
  }
  if (el.classList && el.classList.contains('last-edit-highlight')) {
    return Array.from(el.childNodes)
      .filter((child) => !(child.nodeType === Node.ELEMENT_NODE && child.classList?.contains('last-edit-tooltip')))
      .map((c) => serializeEditorNode(c, tagMap, idCounter))
      .join('');
  }
  if (tagName === 'div' || tagName === 'span') {
    return Array.from(el.childNodes).map((c) => serializeEditorNode(c, tagMap, idCounter)).join('');
  }

  const attrs = readElementAttributes(el);
  const isVoid = VOID_TAGS.has(tagName);
  let id = idCounter.value;
  const existing = Object.keys(tagMap).find((key) => {
    const entry = tagMap[key];
    return entry.tagName === tagName
      && JSON.stringify(entry.attributes) === JSON.stringify(attrs);
  });
  if (existing) {
    id = Number(existing);
  } else {
    idCounter.value += 1;
    id = idCounter.value;
    tagMap[id] = {
      tagName,
      attributes: attrs,
      openTag: buildOpenTagString(tagName, attrs),
      closeTag: isVoid ? '' : buildCloseTagString(tagName),
      isVoid,
    };
  }

  if (isVoid) {
    return MARKER_OPEN(id) + MARKER_CLOSE(id);
  }

  const inner = Array.from(el.childNodes)
    .map((c) => serializeEditorNode(c, tagMap, idCounter))
    .join('');
  return MARKER_OPEN(id) + inner + MARKER_CLOSE(id);
}

export function editorHtmlToDisplay(html, tagMap, entityMap = {}) {
  const doc = getParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body;
  const idCounter = { value: Math.max(0, ...Object.keys(tagMap).map(Number)) };
  let display = '';
  Array.from(root.childNodes).forEach((child) => {
    display += serializeEditorNode(child, tagMap, idCounter);
  });
  return { displayWithMarkers: display, tagMap, entityMap };
}

export { MARKER_REGEX, MARKER_OPEN, MARKER_CLOSE, ENTITY_MARKER_REGEX, ENTITY_MARKER };
